import React, { useState, useEffect } from "react";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase/config";
import StudentForm from "../components/StudentForm";
import BatchForm from "../components/BatchForm";
import SubjectForm from "../components/SubjectForm";
import CentreForm from "../components/CentreForm";
import EditStudentForm from "../components/EditStudentForm";
import ZenithForm from "../components/ZenithForm";
import InvoiceForm from "../components/InvoiceForm";
import { useAuth } from "../contexts/AuthContext";
import { deleteStudent } from "../services/studentService";
import { AiFillDelete } from "react-icons/ai";
import toast from "react-hot-toast";
import posthog from 'posthog-js';

function StudentManagementPage() {
  const [activeTab, setActiveTab] = useState("students");
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [centres, setCentres] = useState([]);
  const [students, setStudents] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedCentre, setSelectedCentre] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [showSubjectsDropdown, setShowSubjectsDropdown] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showZenithForm, setShowZenithForm] = useState(false);
  const [selectedStudentForForm, setSelectedStudentForForm] = useState(null);
  const [selectedStudentForHistory, setSelectedStudentForHistory] =
    useState(null);
  const [refreshingStudents, setRefreshingStudents] = useState(false);
  const { user, isFranchise } = useAuth();

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const fetchData = async () => {
    try {
      const [batchesSnap, subjectsSnap, centresSnap, studentsSnap] =
        await Promise.all([
          getDocs(collection(db, "batches")),
          getDocs(collection(db, "subjects")),
          getDocs(collection(db, "centres")),
          getDocs(collection(db, "students")),
        ]);

      setBatches(
        batchesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      );
      setSubjects(
        subjectsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      );
      setCentres(
        centresSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      );
      setStudents(
        studentsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      );
      if (isFranchise)
        setSelectedCentre(capitalizeFirstLetter(user.email.split("@")[0]));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      posthog.captureException(error, {
        tags: {
          service: 'student_management_page',
          operation: 'fetch_data',
          user_id: user?.uid
        }
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const refreshStudentData = async () => {
    setRefreshingStudents(true);
    try {
      const studentsSnap = await getDocs(collection(db, "students"));
      const updatedStudents = studentsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setStudents(updatedStudents);
      
      if (selectedStudentForHistory) {
        const updatedSelectedStudent = updatedStudents.find(s => s.id === selectedStudentForHistory.id);
        if (updatedSelectedStudent) {
          setSelectedStudentForHistory(updatedSelectedStudent);
        }
      }
      
      toast.success("Student data refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing student data:", error);
      posthog.captureException(error, {
        tags: {
          service: 'student_management_page',
          operation: 'refresh_student_data',
          user_id: user?.uid
        }
      });
      toast.error("Error refreshing student data. Please try again.");
    } finally {
      setRefreshingStudents(false);
    }
  };

  const handleStudentAdded = (newStudent) => {
    setStudents((prev) => [...prev, newStudent]);
    setShowAddForm(false);
  };

  const handleStudentUpdate = (updatedStudent) => {
    setStudents(
      students.map((student) =>
        student.id === updatedStudent.id ? updatedStudent : student,
      ),
    );
    setSelectedStudent(null);
  };

  const handleClickOutside = (e) => {
    if (e.target.className === "modal-overlay") {
      setShowAddForm(false);
      setSelectedStudent(null);
    }
  };

  const handleSubjectsDropdownClickOutside = (e) => {
    if (!e.target.closest('.subjects-dropdown')) {
      setShowSubjectsDropdown(false);
    }
  };

  useEffect(() => {
    if (showSubjectsDropdown) {
      document.addEventListener('click', handleSubjectsDropdownClickOutside);
      return () => {
        document.removeEventListener('click', handleSubjectsDropdownClickOutside);
      };
    }
  }, [showSubjectsDropdown]);

  const handleSubjectToggle = (subject) => {
    setSelectedSubjects(prev => 
      prev.includes(subject) 
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  };

  const clearSubjectFilters = () => {
    setSelectedSubjects([]);
  };

  const filteredStudents = students.filter((student) => {
    const normalizedCentre = selectedCentre?.replace(/\s+/g, "");
    const matchesName = student.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesBatch = selectedBatch
      ? student.batch.includes(selectedBatch)
      : true;
    const matchesCentre = selectedCentre
      ? student.centres?.map((centre) => centre.replace(/\s+/g, ""))
          .includes(normalizedCentre)
      : true;
    const matchesSubjects = selectedSubjects.length > 0
      ? selectedSubjects.length === student.subjects?.length &&
        selectedSubjects.every(subject => 
          student.subjects?.includes(subject)
        )
      : true;
    
    // Date range filter
    const matchesDateRange = () => {
      if (!startDate && !endDate) return true;
      
      // Handle Firestore timestamps or regular date strings
      let studentDate;
      if (student.createdAt) {
        if (typeof student.createdAt.toDate === 'function') {
          studentDate = student.createdAt.toDate();
        } else if (student.createdAt?.seconds) {
          studentDate = new Date(student.createdAt.seconds * 1000);
        } else {
          studentDate = new Date(student.createdAt);
        }
      } else {
        return true; // If no date, include the student
      }
      
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate + "T23:59:59") : null; // Include the entire end date
      
      if (start && end) {
        return studentDate >= start && studentDate <= end;
      } else if (start) {
        return studentDate >= start;
      } else if (end) {
        return studentDate <= end;
      }
      return true;
    };
    
    return matchesName && matchesBatch && matchesCentre && matchesSubjects && matchesDateRange();
  });

  const handleDelete = async (student) => {
    if (window.confirm("Are you sure you want to delete this student?")) {
      try {
        await deleteDoc(doc(db, "students", student.id));
        await deleteStudent(student.email);
        setStudents(students.filter((stud) => stud.id !== student.id));
      } catch (error) {
        console.error("Error deleting student:", error);
        posthog.captureException(error, {
          tags: {
            service: 'student_management_page',
            operation: 'delete_student',
            user_id: user?.uid,
            student_email: student.email
          },
          extra: {
            student_name: student.name,
            batch: student.batch
          }
        });
        toast.error("Error deleting student. Please try again.");
      }
    }
  };

  const handleEmailClick = (student) => {
    // Create a new URL with student data as query parameters
    const params = new URLSearchParams({
      studentData: JSON.stringify(student),
    });

    // Open in new tab
    window.open(`/receipt?${params.toString()}`, "_blank");
  };

  const PaymentHistoryTooltip = ({ payments }) => {
    if (!payments?.length) {
      return (
        <div style={{ textAlign: "center", color: "#64748b", padding: "20px" }}>
          No payment history recorded.
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {payments.map((payment, index) => (
          <div
            key={index}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: "16px",
              alignItems: "center",
              padding: "16px",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#1e293b",
                  marginBottom: "4px",
                }}
              >
                {new Date(payment.month).toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </div>
              <div
                style={{
                  color: "#718096",
                  fontSize: "12px",
                }}
              >
                Receipt: #{payment.receiptId}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  marginBottom: "4px",
                }}
              >
                ₹{payment.amount}
              </div>
              <div
                style={{
                  color: "#718096",
                  fontSize: "12px",
                  backgroundColor: "#f1f5f9",
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: "12px",
                }}
              >
                {payment.paymentMode}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPaymentHistoryModal = () => {
    if (!selectedStudentForHistory) return null;

    return (
      <div
        className="modal-overlay"
        onClick={(e) => {
          if (e.target.className === "modal-overlay") {
            setSelectedStudentForHistory(null);
          }
        }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <div
          className="modal-content"
          style={{
            backgroundColor: "white",
            borderRadius: "16px",
            width: "100%",
            maxWidth: "600px",
            maxHeight: "90vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <div
            style={{
              padding: "16px 24px",
              paddingRight: "48px",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#1e293b",
              }}
            >
              Payment History for {selectedStudentForHistory.name}
            </h3>
            <button
              onClick={refreshStudentData}
              disabled={refreshingStudents}
              style={{
                padding: "6px 12px",
                backgroundColor: refreshingStudents ? "#e2e8f0" : "#ffa600",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: refreshingStudents ? "not-allowed" : "pointer",
                fontSize: "13px",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
                opacity: refreshingStudents ? 0.7 : 1,
                width: "fit-content",
                minWidth: "80px",
              }}
              onMouseEnter={(e) => {
                if (!refreshingStudents) {
                  e.target.style.backgroundColor = "#e69500";
                }
              }}
              onMouseLeave={(e) => {
                if (!refreshingStudents) {
                  e.target.style.backgroundColor = "#ffa600";
                }
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{
                  animation: refreshingStudents ? "spin 1s linear infinite" : "none",
                }}
              >
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
              </svg>
              {refreshingStudents ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          <button
            onClick={() => setSelectedStudentForHistory(null)}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f1f5f9")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#64748b"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div style={{ overflowY: "auto", padding: "24px" }}>
            <PaymentHistoryTooltip
              payments={selectedStudentForHistory.payments}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderZenithFormModal = () => {
    if (!showZenithForm) return null;

    return (
      <div
        className="modal-overlay"
        onClick={(e) => {
          if (e.target.className === "modal-overlay") {
            setShowZenithForm(false);
            setSelectedStudentForForm(null);
          }
        }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <div
          className="modal-content"
          style={{
            backgroundColor: "white",
            width: "100%",
            height: "100%",
            overflowY: "auto",
          }}
        >
          <ZenithForm
            studentData={selectedStudentForForm}
            onClose={() => {
              setShowZenithForm(false);
              setSelectedStudentForForm(null);
            }}
            onPaymentCreated={refreshStudentData}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          color: "#718096",
        }}
      >
        Loading...
      </div>
    );
  }

  const tabs = isFranchise
    ? ["students", "invoice"]
    : ["students", "batches", "subjects", "centres", "invoice"];

  return (
    <div
      style={{
        padding: "24px",
        maxWidth: "1200px",
        margin: "0 auto",
        backgroundColor: "#f8f9fa",
      }}
    >
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        style={{
          marginBottom: "24px",
          textAlign: "center",
        }}
      >
        <h1
          style={{
            fontSize: "24px",
            color: "#2d3748",
            marginBottom: "8px",
            fontWeight: "600",
          }}
        >
          Student Management
        </h1>
        <p
          style={{
            color: "#718096",
            fontSize: "15px",
          }}
        >
          Manage students, batches, subjects, and centres
        </p>
      </div>

      {/* Tab Navigation */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "24px",
          backgroundColor: "white",
          padding: "8px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              padding: "10px 16px",
              backgroundColor: activeTab === tab ? "#ffa600" : "transparent",
              color: activeTab === tab ? "white" : "#64748b",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s ease",
              textTransform: "capitalize",
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab) {
                e.target.style.backgroundColor = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab) {
                e.target.style.backgroundColor = "transparent";
              }
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "students" && (
        <div>
          {/* Search and Filter Section - Now only visible in students tab */}
          <div style={{ marginBottom: "20px", display: "flex", gap: "10px" }}>
            <input
              type="text"
              placeholder="Search by name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                width: "200px",
              }}
            />
            <select
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                width: "150px",
              }}
            >
              <option value="">All Batches</option>
              {batches.map((batch) => (
                <option key={batch.id} value={batch.name}>
                  {batch.name}
                </option>
              ))}
            </select>
            <select
              value={selectedCentre}
              onChange={(e) => setSelectedCentre(e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                width: "150px",
              }}
              disabled={isFranchise}
            >
              <option value="">All Centres</option>
              {centres.map((centre) => (
                <option key={centre.id} value={centre.name}>
                  {centre.name}
                </option>
              ))}
            </select>
            
            {/* Subjects Multi-Select Dropdown */}
            <div className="subjects-dropdown" style={{ position: "relative", width: "200px" }}>
              <button
                type="button"
                onClick={() => setShowSubjectsDropdown(!showSubjectsDropdown)}
                style={{
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  width: "100%",
                  backgroundColor: "white",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: "14px",
                  color: selectedSubjects.length > 0 ? "#2d3748" : "#64748b",
                }}
              >
                <span>
                  {selectedSubjects.length === 0 
                    ? "All Subjects" 
                    : selectedSubjects.length === 1 
                      ? selectedSubjects[0]
                      : `${selectedSubjects.length} subjects selected`
                  }
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: showSubjectsDropdown ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                >
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </button>
              
              {showSubjectsDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    zIndex: 1000,
                    maxHeight: "200px",
                    overflowY: "auto",
                    marginTop: "4px",
                  }}
                >
                  <div
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e2e8f0",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "500" }}>
                      Select Subjects
                    </span>
                    {selectedSubjects.length > 0 && (
                      <button
                        onClick={clearSubjectFilters}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#dc2626",
                          cursor: "pointer",
                          fontSize: "12px",
                          padding: "2px 6px",
                          borderRadius: "4px",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#fee2e2";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "transparent";
                        }}
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  {subjects.map((subject) => (
                    <label
                      key={subject.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: "14px",
                        transition: "background-color 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f8fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSubjects.includes(subject.name)}
                        onChange={() => handleSubjectToggle(subject.name)}
                        style={{
                          marginRight: "8px",
                          accentColor: "#ffa600",
                        }}
                      />
                      <span style={{ color: "#2d3748" }}>{subject.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            {/* Date Range Filters */}
            <input
              type="date"
              placeholder="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                width: "150px",
              }}
            />
            <input
              type="date"
              placeholder="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                width: "150px",
              }}
            />
            
            {(startDate || endDate) && (
              <button
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#ffa600",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "fit-content",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 6px rgba(255, 166, 0, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Dates
              </button>
            )}
            
            <div>Displaying {filteredStudents?.length} students</div>
          </div>

          {/* Add Student Button */}
          <div
            style={{
              marginBottom: "20px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#ffa600",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "fit-content",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 4px 6px rgba(255, 166, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Student
            </button>
          </div>

          {/* Students Table */}
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#f8fafc",
                      borderBottom: "2px solid #e2e8f0",
                    }}
                  >
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "left",
                        color: "#4a5568",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Name
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "left",
                        color: "#4a5568",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Email
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "left",
                        color: "#4a5568",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Batch
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "left",
                        color: "#4a5568",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Centres
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "left",
                        color: "#4a5568",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Subjects
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "left",
                        color: "#4a5568",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Amount Pend.
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        textAlign: "left",
                        color: "#4a5568",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr key={student.id}>
                      <td
                        style={{
                          padding: "16px 20px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src={student.imageUrl || "/default-avatar.png"}
                          alt={student.name}
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "2px solid #e2e8f0",
                          }}
                        />
                        <span
                          style={{
                            color: "#2d3748",
                            fontWeight: "500",
                            marginLeft: "8px",
                          }}
                        >
                          {student.name}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          cursor: "pointer",
                          color: "#2563eb",
                          textDecoration: "underline",
                        }}
                        onClick={() => handleEmailClick(student)}
                      >
                        {student.email}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          color: "#4a5568",
                        }}
                      >
                        {student.batch}
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            flexWrap: "wrap",
                          }}
                        >
                          {student.centres?.map((centre) => (
                            <span
                              key={centre}
                              style={{
                                backgroundColor: "#f1f5f9",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                color: "#64748b",
                              }}
                            >
                              {centre}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            flexWrap: "wrap",
                          }}
                        >
                          {student.subjects?.map((subject) => (
                            <span
                              key={subject}
                              style={{
                                backgroundColor: "#f1f5f9",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                color: "#64748b",
                              }}
                            >
                              {subject}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          color: "#4a5568",
                          fontWeight: student.amountPending > 0 ? "600" : "400",
                        }}
                      >
                        <span
                          style={{
                            color:
                              student.amountPending > 0 ? "#dc2626" : "#16a34a",
                            backgroundColor:
                              student.amountPending > 0 ? "#fee2e2" : "#f0fdf4",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "14px",
                          }}
                        >
                          ₹{student.amountPending || 0}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          display: "flex",
                          gap: "8px",
                        }}
                      >
                        <button
                          onClick={() => setSelectedStudent(student)}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "transparent",
                            color: "#ffa600",
                            border: "1px solid #ffa600",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#fff7e6";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "transparent";
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setSelectedStudentForHistory(student)}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "transparent",
                            color: "#2563eb",
                            border: "1px solid #2563eb",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            transition: "all 0.2s ease",
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#eff6ff";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "transparent";
                          }}
                        >
                          Bill
                        </button>
                        {!isFranchise && (
                          <button
                            onClick={() => handleDelete(student)}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "transparent",
                              color: "#dc2626",
                              border: "1px solid #dc2626",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#fee2e2";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "transparent";
                            }}
                          >
                            <AiFillDelete />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal Forms */}
          {showAddForm && (
            <div
              className="modal-overlay"
              onClick={handleClickOutside}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
              }}
            >
              <div
                className="modal-content"
                style={{
                  backgroundColor: "white",
                  padding: "32px",
                  borderRadius: "16px",
                  width: "100%",
                  maxWidth: "500px",
                  maxHeight: "90vh",
                  overflowY: "auto",
                }}
              >
                <StudentForm
                  onStudentAdded={handleStudentAdded}
                  onClose={() => setShowAddForm(false)}
                  batches={batches}
                  subjects={subjects}
                  centres={centres}
                />
              </div>
            </div>
          )}

          {selectedStudent && (
            <div
              className="modal-overlay"
              onClick={handleClickOutside}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
              }}
            >
              <div
                className="modal-content"
                style={{
                  backgroundColor: "white",
                  padding: "32px",
                  borderRadius: "16px",
                  width: "100%",
                  maxWidth: "500px",
                  maxHeight: "90vh",
                  overflowY: "auto",
                }}
              >
                <EditStudentForm
                  student={selectedStudent}
                  onClose={() => setSelectedStudent(null)}
                  onUpdate={handleStudentUpdate}
                  batches={batches}
                  subjects={subjects}
                  centres={centres}
                />
              </div>
            </div>
          )}
          {renderZenithFormModal()}
          {renderPaymentHistoryModal()}
        </div>
      )}

      {/* Other tabs content */}
      {activeTab === "batches" && (
        <div
          style={{
            background: "white",
            padding: "24px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <BatchForm batches={batches} setBatches={setBatches} />
        </div>
      )}
      {activeTab === "subjects" && (
        <div
          style={{
            background: "white",
            padding: "24px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <SubjectForm subjects={subjects} setSubjects={setSubjects} />
        </div>
      )}
      {activeTab === "centres" && (
        <div
          style={{
            background: "white",
            padding: "24px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <CentreForm centres={centres} setCentres={setCentres} />
        </div>
      )}
      {activeTab === "invoice" && (
        <div
          style={{
            background: "white",
            padding: "24px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <InvoiceForm students={students} batches={batches} subjects={subjects} />
        </div>
      )}
    </div>
  );
}

export default StudentManagementPage;
